/**
 * A STALE CHUNK IS NOT AN ERROR — IT IS AN OLD TAB.
 *
 * The staff surface is code-split, so a tab left open across a publish asks for
 * a file name the new build no longer has. The import rejects, the error
 * boundary catches a TypeError, and the browser pager files it as an incident.
 * Both reactions are wrong: the fix is already on the server, and paging on
 * every publish is how a pager gets ignored — the same pager that took five
 * weeks to notice was dead.
 *
 * So this module does two things and nothing else:
 *   1. RECOGNISES the shape (a failed dynamic import / preload), which is
 *      distinguishable from a real render error.
 *   2. RELOADS ONCE, guarded by a per-tab marker, so a genuinely broken build
 *      cannot become a reload loop. The second failure is a real error and is
 *      allowed through to the boundary and to the pager.
 */

const RELOAD_MARKER = "cardmoji.chunkReload";

/**
 * The message text varies by browser and by whether it was a preload or the
 * import itself, so match on the family rather than one engine's wording:
 * Chrome "Failed to fetch dynamically imported module", Firefox "error loading
 * dynamically imported module", Safari "Importing a module script failed",
 * Vite's own preload helper, and the `.js`-chunk MIME/404 variants.
 */
export function isStaleChunkError(err: unknown): boolean {
  const msg =
    err instanceof Error
      ? `${err.name}: ${err.message}`
      : typeof err === "string"
        ? err
        : "";
  if (!msg) return false;
  return (
    /failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /importing a module script failed/i.test(msg) ||
    /failed to import/i.test(msg) ||
    /dynamic import|dynamically imported/i.test(msg) ||
    /loading chunk \d+ failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg) ||
    /unable to preload css/i.test(msg)
  );
}

/** True while this tab has already spent its single automatic reload. */
export function hasReloadedForStaleChunk(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_MARKER) !== null;
  } catch {
    // No sessionStorage (private mode, blocked storage) means no loop guard is
    // possible, so treat it as already spent: showing the error beats risking
    // an endless reload.
    return true;
  }
}

/**
 * Reload once for a stale chunk. Returns false when the reload was NOT taken —
 * the caller must then let the error surface normally.
 */
export function reloadOnceForStaleChunk(reason: string): boolean {
  if (hasReloadedForStaleChunk()) return false;
  try {
    sessionStorage.setItem(RELOAD_MARKER, JSON.stringify({ at: Date.now(), reason }));
  } catch {
    return false;
  }
  // `true` forces the document past any cached HTML holding the old asset names.
  window.location.reload();
  return true;
}

/**
 * Wrap a `lazy()` import so a stale-chunk rejection reloads instead of
 * rendering the error boundary. The returned promise never resolves in the
 * reload case, which is correct: the document is being replaced.
 */
export function withDeployReload<T>(load: () => Promise<T>): () => Promise<T> {
  return () =>
    load().catch((err) => {
      if (isStaleChunkError(err) && reloadOnceForStaleChunk("lazy-import")) {
        return new Promise<T>(() => {
          /* the page is navigating; resolving would race the reload */
        });
      }
      throw err;
    });
}

/**
 * Vite reports a failed preload on the window rather than only on the import,
 * and a stale chunk can also surface as an unhandled rejection from a prefetch
 * that no component awaited. Both are the same condition.
 */
export function installStaleChunkReload() {
  window.addEventListener("vite:preloadError", (event) => {
    if (reloadOnceForStaleChunk("vite:preloadError")) event.preventDefault();
  });
  window.addEventListener("unhandledrejection", (event) => {
    if (isStaleChunkError(event.reason) && reloadOnceForStaleChunk("unhandledrejection")) {
      event.preventDefault();
    }
  });
}
